/**
 * wizardValidation.js — the NewCaseWizard step validators, kept as a pure
 * module (no JSX) so `node --test` can exercise them directly. The wizard
 * imports these; nothing else should.
 *
 * Every rule here BLOCKS submission (unlike the completion matrix in
 * eventVocabularies.getRequirements, which only lists what a case needs to be
 * complete). Spec: required_evidence_logic.md §3–§5, submission_vs_case_logic.md.
 */
import { SUBTYPE_OTHER } from '@shared/incidentCategoryCascade.js';
import { REFUSAL_REGISTER_CATEGORY, REFUSAL_DATETIME_RE } from '@shared/refusalRegister.js';
import { treatmentHasCodes, submitEvidenceRequired, reviewDateRequired, submitRequiredFields, FIELD_META } from './eventVocabularies.js';

// Blocking validation is LIVE (v0.44.0) — the pre-release suspension is over now
// that the app has been in production for a while. ENFORCE_REQUIRED stays as an
// emergency kill-switch: flipping it false demotes every * field to advisory.
export const ENFORCE_REQUIRED = true;

export const REVIEW_DATE_HELP = 'Once the review has taken place, please update the case file on AE Track by adding a comment.';

// A coded treatment (LHR/AWI) whose severity dropdown has NO options is an
// admin misconfiguration, not a form the user can complete — the wizard shows
// this as a blocking callout and the server 400s the submission regardless.
export const SEVERITY_NOT_CONFIGURED = 'Severity codes are not configured for this treatment — contact an administrator.';

const blank = (v) => !String(v ?? '').trim();

export function validateStep1(form, { clinicUnavailable = false } = {}) {
  if (!ENFORCE_REQUIRED) return {};
  const errors = {};
  // Near-miss and clinic-incident reports have no client phone field, and their
  // Title is an incident title rather than a client name.
  const isNonTreatment = form.EventType === 'NearMiss' || form.EventType === 'ClinicIncident';
  if (!form.EventType)        errors.EventType = 'Event type is required.';
  if (blank(form.Title)) {
    errors.Title = isNonTreatment
      ? 'Incident title is required.'
      : 'Client name is required.';
  }
  if (!isNonTreatment && blank(form.ClientPhoneNumber)) {
    errors.ClientPhoneNumber = 'Phone number is required.';
  }
  if (!isNonTreatment && blank(form.StaffMemberConcerned)) {
    errors.StaffMemberConcerned = 'Staff member is required — enter N/A if no staff member was involved.';
  }
  // A clinic-list outage must not stop a report: with the list failed the field
  // is unfillable, so it degrades to advisory rather than deadlocking Step 1.
  if (!form.Clinic && !clinicUnavailable) errors.Clinic = 'Clinic is required.';
  if (!form.DateofIncident)   errors.DateofIncident = 'Date of incident is required.';
  return errors;
}

/**
 * @param {object} form            — wizard form state
 * @param {boolean} hasSubtypes    — the selected treatment declares subtypes
 * @param {object}  opts
 *   hasTreatmentOptions — the treatment dropdown has options (admin config can empty it)
 *   hasSeverityOptions  — the severity dropdown has options. For a CODED
 *                         treatment an empty list is a misconfiguration and
 *                         blocks with SEVERITY_NOT_CONFIGURED (the server
 *                         rejects a coded case without a code either way)
 *   hasConsentUpload    — a consent_form file is queued for upload
 *   hasCategoryOptions  — the RESOLVED incident-category list is non-empty (AWI
 *                         subtype "Other" yields none; config can empty a cell)
 *
 * Keys are emitted in visual order — the wizard focuses the first one.
 */
export function validateStep2(form, hasSubtypes = false, {
  hasTreatmentOptions = true,
  hasSeverityOptions  = true,
  hasConsentUpload    = false,
  hasCategoryOptions  = false,
} = {}) {
  if (!ENFORCE_REQUIRED) return {};
  const errors = {};
  if (form.EventType === 'Treatment') {
    // A vocabulary scoped away from this event type in admin config must not
    // deadlock reporting: a dropdown is only required while it has options.
    if (!form.Department && hasTreatmentOptions) errors.Department = 'Treatment type is required.';
    if (treatmentHasCodes(form.Department)) {
      if (!hasSeverityOptions)   errors.Severity = SEVERITY_NOT_CONFIGURED;
      else if (!form.Severity)   errors.Severity = 'Severity code is required.';
    }
    if (hasSubtypes && !form.TreatmentSubtype) errors.TreatmentSubtype = `A subtype must be selected for ${form.Department}.`;
    if (form.TreatmentSubtype === SUBTYPE_OTHER && blank(form.TreatmentSubtypeOther)) {
      errors.TreatmentSubtypeOther = 'Please describe the other subtype.';
    }
    // Incident category is mandatory whenever there is something to pick
    // (required_evidence_logic.md §4); 'Other' still needs the free text.
    if (hasCategoryOptions && !form.IncidentCategory) {
      errors.IncidentCategory = 'Incident category is required.';
    }
    if (form.IncidentCategory === 'Other' && blank(form.IncidentCategoryOther)) {
      errors.IncidentCategoryOther = 'Please describe the other category.';
    }
    // Review date — LHR/AWI Code 3–5 (required_evidence_logic.md §5). Past and
    // future dates are both fine (the review may already have happened).
    if (reviewDateRequired(form.Department, form.Severity) && !form.ReviewDate) {
      errors.ReviewDate = 'Review date is required for Code 3–5 cases.';
    }
    // Treatment-specific fields that must be filled to submit (AWI: product,
    // area, dosage, symptoms). Error keys match the ExtraField input ids so the
    // first-error focus jump lands on the input.
    for (const k of submitRequiredFields(form.Department)) {
      if (blank((form.TypeSpecific || {})[k])) errors[`ts-${k}`] = `${FIELD_META[k]?.label || k} is required.`;
    }
    // Consent form — EVERY Treatment submission: upload it, or switch on "No
    // consent form" and say why (required_evidence_logic.md §3).
    if (!hasConsentUpload) {
      if (form.ConsentWaived) {
        if (blank(form.ConsentWaiverReason)) errors.ConsentEvidence = 'Record why there is no consent form.';
      } else {
        errors.ConsentEvidence = 'Upload the consent form, or record that there is none.';
      }
    }
    // Laser settings — LHR Code 1–2 only: type them in, or declare none recorded.
    if (submitEvidenceRequired(form.Department, form.Severity)) {
      const ts = form.TypeSpecific || {};
      if (blank(ts.laser_settings) && ts.no_laser_settings !== true) {
        errors.LaserSettingsEvidence = 'Enter the laser settings, or tick "No settings recorded".';
      }
    }
  } else if (form.EventType === 'NearMiss') {
    if (!form.NM_ReportingFunction)       errors.NM_ReportingFunction       = 'Reporting function is required.';
    if (!form.NM_Category)                errors.NM_Category                = 'Category is required.';
    if (form.NM_Category === 'Other' && blank(form.NM_CategoryOther)) {
      errors.NM_CategoryOther = 'Please describe the other category.';
    }
    if (form.NM_Category === REFUSAL_REGISTER_CATEGORY) {
      // Refusal Register — the five register items, all blocking. It closes on
      // submission, so there is no cause / corrective action / assignee / target
      // date to fill in (refusal_register_logic.md).
      if (blank(form.RR_RequestedAt))         errors.RR_RequestedAt       = 'Date and time of the request is required.';
      else if (!REFUSAL_DATETIME_RE.test(String(form.RR_RequestedAt).trim())) {
        errors.RR_RequestedAt = 'Enter both the date and the time of the request.';
      }
      if (blank(form.NM_Description))         errors.NM_Description       = 'Details of the incident are required.';
      if (blank(form.RR_BuyerDescription))    errors.RR_BuyerDescription  = 'A description of the potential buyer is required.';
      if (blank(form.RR_ActionTaken))         errors.RR_ActionTaken       = 'Record the action taken.';
      if (blank(form.StaffMemberConcerned))   errors.StaffMemberConcerned = 'The staff member who refused the request is required.';
    } else {
      if (blank(form.NM_Description))      errors.NM_Description             = 'Description is required.';
      if (blank(form.NM_Cause))            errors.NM_Cause                   = 'Cause is required.';
      if (blank(form.NM_CorrectiveAction)) errors.NM_CorrectiveAction        = 'Corrective action is required.';
      if (!form.NM_AssigneeRole)            errors.NM_AssigneeRole            = 'Assignee is required.';
      if (form.NM_AssigneeRole === 'Other' && blank(form.NM_AssigneeOther)) {
        errors.NM_AssigneeOther = 'Please specify the assignee.';
      }
      if (!form.NM_TargetResolutionDate)    errors.NM_TargetResolutionDate    = 'Target resolution date is required.';
    }
  } else if (form.EventType === 'ClinicIncident') {
    if (!form.CI_IncidentType)            errors.CI_IncidentType            = 'Type of incident is required.';
    if (form.CI_IncidentType === 'Other' && blank(form.CI_IncidentTypeOther)) {
      errors.CI_IncidentTypeOther = 'Please describe the incident type.';
    }
    if (!form.CI_PersonStatus)            errors.CI_PersonStatus            = 'Injured person status is required.';
    if (blank(form.CI_Description))       errors.CI_Description             = 'A description is required.';
  }
  return errors;
}
